<template>
  <div v-if="!isRatioFree" class="nc-image-container" :class="blur ? 'tw-overflow-hidden' : ''" :style="styleVars">
    <div v-if="blur" class="tw-absolute tw-inset-0 tw-z-30 tw-bg-white/20"></div>
    <NuxtImg
      v-if="!imageError"
      :key="`image-${imageURL}`"
      :src="imageURL"
      class="nc-image"
      :class="imageClass"
      :style="style"
      loading="lazy"
      :quality="dynamicQuality"
      :title="title"
      :alt="alt"
      format="webp"
      @error="handleImageError"
    ></NuxtImg>
    <img
      v-else
      :src="fallbackImageURL"
      :key="`image-${fallbackImageURL}`"
      class="nc-image"
      :class="imageClass"
      :style="style"
      loading="lazy"
      :title="title"
      :alt="alt"
      @error="handleFallbackError"
    />
    <div class="nc-overlay-item-on-image">
      <slot name="overlay-item">
        <div v-if="blur" class="tw-text-center pre-market-property-badge">
          <h3 v-html="preMarketBlurText" class="tw-text-white"></h3>
        </div>
      </slot>
    </div>
  </div>
  <template v-else-if="isRatioFree">
    <NuxtImg
      v-if="!imageError"
      :key="`image-${imageURL}`"
      :src="imageURL"
      class="nc-image"
      :class="imageClass"
      :style="style"
      loading="lazy"
      :quality="dynamicQuality"
      :title="title"
      :alt="alt"
      format="webp"
      @error="handleImageError"
    ></NuxtImg>
    <img
      v-else
      :src="fallbackImageURL"
      :key="`image-${fallbackImageURL}`"
      class="nc-image"
      :class="imageClass"
      :style="style"
      loading="lazy"
      :title="title"
      :alt="alt"
      @error="handleFallbackError"
    />
  </template>
</template>

<script lang="ts">
import { mapState } from 'pinia';
import { produceKey } from '~/server/core/helpers';
import { useWebsiteStore } from '~/units/website/store';

function calculateAspectRatio(width: number, height: number) {
  // Helper function to find the Greatest Common Divisor (GCD) using the Euclidean algorithm
  function gcd(a: number, b: number): number {
    return b === 0 ? a : gcd(b, a % b);
  }

  // Calculate the GCD of the width and height
  const gcdValue = gcd(width, height);

  // Simplify the width and height by dividing by the GCD
  const simplifiedWidth = width / gcdValue;
  const simplifiedHeight = height / gcdValue;

  // Return the aspect ratio as an object with separate width and height properties
  return {
    width: simplifiedWidth,
    height: simplifiedHeight,
  };
}

export default defineNuxtComponent({
  name: 'LibraryImage',

  props: {
    src: {
      required: false,
      type: String,
    },

    hRatio: {
      required: false,
      type: Number,
      default: 2,
    },
    wRatio: {
      required: false,
      type: Number,
      default: 3,
    },

    mobileHRatio: {
      required: false,
      type: Number,
      default: null,
      description: 'Component uses hRatio if this is not specified',
    },

    mobileWRatio: {
      required: false,
      type: Number,
      default: null,
      description: 'Component uses wRatio if this is not specified',
    },

    quality: {
      required: false,
      type: Number,
      default: 40,
    },

    overlayItemPosition: {
      required: false,
      type: String,
      default: 'center',
      supports: 'Only supports "center" value',
    },

    title: {
      required: false,
      type: String,
      default: '',
    },

    alt: {
      required: false,
      type: String,
      default: '',
    },

    isRatioFree: {
      required: false,
      type: Boolean,
      description: 'set this prop true if you dont want to use ratio - suggested for inline blocks',
    },

    style: {
      required: false,
      type: [String, Object],
    },

    designWidth: {
      required: false,
      type: Number,
    },

    designHeight: {
      required: false,
      type: Number,
    },

    smHRatio: {
      required: false,
      type: Number,
      default: null,
      description: 'Component uses hRatio if this is not specified',
    },

    smWRatio: {
      required: false,
      type: Number,
      default: null,
      description: 'Component uses wRatio if this is not specified',
    },

    blur: {
      required: false,
      type: String,
      default: null,
    },

    preMarketBlurText: {
      required: false,
      type: String,
      default: 'Pre-Market<br />Property',
    },

    useDefaultRadius: {
      required: false,
      type: Boolean,
      default: false,
    },

    imageClass: {
      required: false,
      type: String,
      default: ""
    }
  },

  data() {
    return {
      imageError: false,
      key: produceKey(),

    };
  },

  computed: {
    ...mapState(useWebsiteStore, ['theme', 'company']),

    /**
     * @description Dynamic quality based on company-specific requirements
     */
    dynamicQuality() {
      const companyQualitySettings: Record<string, number> = {
        brownsestateagents: 100,
        // example: { anothercompany: 80 }
      };

      const companyKey = this.company?.company_key;
      
      return (companyKey && companyQualitySettings[companyKey]) || this.quality;
    },

    /**
     * @description some image urls have empty space at the beginning of string and it causes connection issue when we use vercel&nuxt-image and plugin adds + instead of empty space. This allows us to show image properly.
     */ imageURL() {
      if (this.src) {
        return this.src.trim();
      }
      return this.src;
    },

    /**
     * @description Fallback image URL with quality parameter to maintain consistency with NuxtImg
     */
    fallbackImageURL() {
      if (!this.imageURL) return this.imageURL;
      
      try {
        const url = new URL(this.imageURL);
        
        // Only add quality parameter if it doesn't already exist
        if (!url.searchParams.has('q') && !url.searchParams.has('quality')) {
          url.searchParams.set('q', this.dynamicQuality.toString());
        }
        
        return url.toString();
      } catch (error) {
        // If URL parsing fails (relative URL or invalid URL), return original
        // For relative URLs, append quality as query parameter
        const separator = this.imageURL.includes('?') ? '&' : '?';
        return `${this.imageURL}${separator}q=${this.dynamicQuality}`;
      }
    },

    imageRatio(): number {
      if (this.designHeight && this.designWidth) {
        const result = calculateAspectRatio(this.designWidth, this.designHeight);
        return (result.height / result.width) * 100;
      }

      return (this.hRatio / this.wRatio) * 100;
    },

    imageMobileRatio(): number {
      if (this.mobileWRatio && this.mobileHRatio) {
        return (this.mobileHRatio / this.mobileWRatio) * 100;
      }
      return this.imageRatio;
    },

    imageSMRatio(): number {
      if (this.smWRatio && this.smHRatio) {
        return (this.smHRatio / this.smWRatio) * 100;
      }
      return this.imageRatio;
    },

    styleVars() {
      return {
        '--nc-image-ratio': `${this.imageRatio}%`,
        '--nc-mobile-image-ratio': `${this.imageMobileRatio}%`,
        '--nc-sm-image-ratio': `${this.imageSMRatio}%`,
        '--nc-image-blur-filter': `blur(${this.blur})`,
        'border-radius': this.useDefaultRadius ? 'var(--rounded-sm)' : undefined,
      };
    },
  },

  methods: {
    handleImageError() {
      console.warn('NuxtImg failed to load, falling back to regular img element:', this.imageURL);
      this.imageError = true;
    },

    handleFallbackError() {
      console.error('Both NuxtImg and regular img failed to load:', this.imageURL);
    }
  },

  watch: {
    src() {
      // Reset error state when src changes
      this.imageError = false;
    }
  }
});
</script>

<style scoped>
.nc-image-container {
  position: relative;
  width: 100%; /* This makes the container responsive */
  padding-bottom: var(--nc-image-ratio); /* This maintains aspect ratio */
}

@media (max-width: 1023px) {
  .nc-image-container {
    padding-bottom: var(--nc-mobile-image-ratio); /* This maintains aspect ratio on mobile */
  }
}

@media (max-width: 768px) {
  .nc-image-container {
    padding-bottom: var(--nc-sm-image-ratio); /* This maintains aspect ratio on mobile */
  }
}

.nc-image-container .nc-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover; /* This ensures the image covers the container */
  filter: var(--nc-image-blur-filter);
}

.nc-overlay-item-on-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
}
</style>
